import { CostUnit, RehabAction } from '@Types';
import * as uuid from 'uuid';
import {
	Drawer,
	closeDrawer,
	HTMLInputElementExtended,
	HTMLTextAreaElementExtended,
} from '@innovyze/stylovyze';
import { Grid, TextField } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { selectRehabActions } from '@Selectors/Rehab.selectors';
import {
	addNewRehabActionToTree,
	addRehabActionResolved,
	saveRehabActionResolved,
	setIsDirty,
	updateRehabActionInTree,
} from '@Actions/Rehab.actions';
import { useGlobalization } from '@Translations';

interface Props {
	treeId: string;
	rehabAction: RehabAction | undefined | null;
	shouldAddNewActionToTree: () => boolean;
}

export const RehabActionDrawer = ({
	treeId,
	rehabAction,
	shouldAddNewActionToTree,
}: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const [validName, setValidName] = useState<string>(
		rehabAction ? '' : 'Enter a valid name',
	);
	const [name, setName] = useState<string>(
		rehabAction ? rehabAction.actionId : '',
	);

	const [notes, setNotes] = useState<string>(
		rehabAction ? rehabAction.notes : '',
	);

	const rehabActions = selectRehabActions();

	const updateValue = (
		event: ChangeEvent<
			| HTMLInputElement
			| HTMLInputElementExtended
			| HTMLTextAreaElementExtended
		>,
		sField: 'Name' | 'Notes',
	) => {
		switch (sField) {
			case 'Name':
				{
					const sValue = event.target.value.trim();
					let error = '';
					if (sValue.length == 0) {
						error = t('Enter a valid name');
					} else {
						const rexp = new RegExp(/^[a-zA-Z0-9 ,._@-]*$/);
						const test = rexp.test(sValue);
						if (!test) {
							error = t(
								'Only letters, numbers, spaces and some special characters (,._@-) are allowed',
							);
						} else if (!rehabAction) {
							const valid =
								rehabActions?.find(
									rehabAction =>
										rehabAction.actionId.toUpperCase() ===
										sValue.toUpperCase(),
								) === undefined;
							if (!valid) error = t('Enter a unique name');
						} else if (sValue != rehabAction.actionId) {
							const valid =
								rehabActions?.find(
									rehabAction =>
										rehabAction.actionId.toUpperCase() ===
										sValue.toUpperCase(),
								) === undefined;
							if (!valid) error = t('Enter a unique name');
						}
					}

					setValidName(error);
					setName(event.target.value);
				}
				break;
			case 'Notes': {
				setNotes(event.target.value);
				break;
			}
			default:
				break;
		}
	};

	const clearFields = () => {
		setName('');
		setValidName('');
		setNotes('');
	};

	const nameInputs = (
		<Grid xs={12} item>
			<TextField
				fullWidth
				required
				name="Name"
				onChange={(
					// eslint-disable-next-line prettier/prettier
					event: ChangeEvent<HTMLInputElement>,
				) => updateValue(event, 'Name')}
				value={name}
				label={t('Name')}
				variant="outlined"
				helperText={validName}
				error={validName != ''}
			/>
		</Grid>
	);

	const notesInputs = (
		<Grid xs={12} item>
			<TextField
				fullWidth
				name="Notes"
				onChange={(
					// eslint-disable-next-line prettier/prettier
					event: ChangeEvent<HTMLInputElement>,
				) => updateValue(event, 'Notes')}
				value={notes}
				label={t('Description')}
				variant="outlined"
			/>
		</Grid>
	);

	const handleAddOrSave = () => {
		if (name != '') {
			if (!rehabAction) {
				const newRehabAction: RehabAction = {
					_id: uuid.v4(),
					actionId: name.trim(),
					treeId: treeId,
					unitCost: 0,
					costUnit: 'USD', //"USD" or "GBP", now is not used
					unit: CostUnit.length,
					settings: {
						enabled: true,
						defects: [],
					},
					notes: notes.trimEnd(),
				};
				dispatch(addRehabActionResolved(newRehabAction));
				if (shouldAddNewActionToTree())
					dispatch(addNewRehabActionToTree(newRehabAction));
				setIsDirty(true);
			} else {
				if (rehabAction && rehabAction._id != '') {
					const modifiedRehabAction: RehabAction = {
						_id: rehabAction._id,
						actionId: name,
						treeId: treeId,
						unitCost: 0,
						costUnit: 'USD', //"USD" or "GBP", now is not used
						unit: CostUnit.length,
						settings: {
							enabled: true,
							defects: [],
						},
						notes: notes,
					};
					dispatch(saveRehabActionResolved(modifiedRehabAction));
					dispatch(updateRehabActionInTree(modifiedRehabAction));
					dispatch(setIsDirty(true));
				}
			}
			return true;
		} else return false;
	};

	return (
		<Drawer
			action={{
				label: rehabAction ? t('Save') : t('Add'),
				onClick: function noRefCheck() {
					const valid = handleAddOrSave();
					if (valid) {
						dispatch(closeDrawer());
					}
				},
				disabled: validName != '',
			}}
			drawerId="addNewActionDrawer"
			onCancel={() => dispatch(closeDrawer())}
			onOpen={() => {
				if (rehabAction) {
					setName(rehabAction.actionId);
					setValidName('');
					setNotes(rehabAction.notes);
				} else {
					clearFields();
				}
			}}
			title={rehabAction ? t('Edit Group') : t('New Group')}>
			<form noValidate autoComplete="off">
				<Grid container spacing={2}>
					{nameInputs}
					{notesInputs}
				</Grid>
			</form>
		</Drawer>
	);
};
